import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import * as appInsightUtils from '../../../utils/appInsightUtils';
import { AxiosError } from 'axios';
import { useAuth } from '../AuthContext/AuthContext';
import AppInsightsContextType from '../../../@types/AppInsightsContext.type';

const AppInsightsContext = createContext<AppInsightsContextType | undefined>(
  undefined,
);

export const AppInsightProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const userAuth = useAuth();
  const userDatas = userAuth?.userData;

  const trackPageView = (pageName: string) => {
    appInsightUtils.trackPageView(
      pageName,
      userDatas?.profile?.email as string,
    );
  };

  const trackEvent = (eventName: string) => {
    appInsightUtils.trackEvent(eventName, userDatas?.profile?.email as string);
  };

  const trackError = (error: AxiosError | Error) => {
    appInsightUtils.trackError(error);
  };

  const logInformation = (
    message: string,
    moduleName: string,
    functionName: string,
  ) => {
    appInsightUtils.logInformation(message, moduleName, functionName);
  };

  const logError = (
    message: string,
    moduleName: string,
    functionName: string,
    customProperties: object,
  ) => {
    appInsightUtils.logError(
      message,
      moduleName,
      functionName,
      customProperties,
    );
  };

  useEffect(() => {
    if (userDatas && !userAuth?.isLoading) {
      appInsightUtils.setAuthenticatedUser(userDatas?.profile?.email as string);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDatas?.profile, userAuth?.isLoading]);

  const contextValue = useMemo(
    () => ({
      trackPageView,
      trackEvent,
      trackError,
      logInformation,
      logError,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userDatas?.profile?.email],
  );

  return (
    <AppInsightsContext.Provider value={contextValue}>
      {children}
    </AppInsightsContext.Provider>
  );
};

export const useAppInsightConsumer = () => {
  const context = useContext(AppInsightsContext);

  if (!context) {
    throw new Error(
      'useAppInsightConsumer must be used within a AppInsightsContext',
    );
  }
  return context;
};
